import dayjs from "dayjs";
import { fetchFromBusinessCentral } from "../utils/businessCentralUtils";
import { findIdentityDocument } from "../constants/identityDocuments";

// BC devuelve el tipo de documento como enum técnico en inglés (ID/VAT/Passport) en vez
// del valor de negocio (Cedula/RUC/Pasaporte). Solo normalizamos si se conoce el país,
// para no aplicar por error los alias de un país a los datos de otro.
const resolveDocumentType = (raw, countryCode) => {
    if (!raw) return raw ?? "";
    if (!countryCode) return raw;
    return findIdentityDocument(countryCode, raw)?.value || raw;
};

// Builders
export function buildSyncMessage(invoiceCount, creditMemoCount, postedInvoiceCount, postedCreditMemosCount) {
    if (invoiceCount + creditMemoCount + postedInvoiceCount + postedCreditMemosCount === 0) {
        return "No se encontraron documentos históricos para los filtros seleccionados.";
    }

    let parts = [];

    if (invoiceCount > 0) {
        parts.push(
            invoiceCount === 1
                ? "1 factura fue sincronizada correctamente"
                : `${invoiceCount} facturas fueron sincronizadas correctamente`
        );
    }

    if (creditMemoCount > 0) {
        parts.push(
            creditMemoCount === 1
                ? "1 nota de crédito fue sincronizada correctamente"
                : `${creditMemoCount} notas de crédito fueron sincronizadas correctamente`
        );
    }

    if (postedInvoiceCount > 0) {
        parts.push(
            postedInvoiceCount === 1
                ? "1 factura contabilizada fue sincronizada correctamente"
                : `${postedInvoiceCount} facturas contabilizadas fueron sincronizadas correctamente`
        );
    }

    if (postedCreditMemosCount > 0) {
        parts.push(
            postedCreditMemosCount === 1
                ? "1 nota de crédito contabilizada fue sincronizada correctamente"
                : `${postedCreditMemosCount} notas de crédito contabilizadas fueron sincronizadas correctamente`
        );
    }

    return parts.join(" · ");
}
export function buildFilters(values, isPosted) {
    const fechaInicio = dayjs(values.FechaInicio).format("YYYY-MM-DD");
    const fechaFin = dayjs(values.FechaFin).format("YYYY-MM-DD");

    const filters = [
        `Tienda eq '${values.TPV}'`,
        `${isPosted ? "TPV" : "Caja"} eq '${values.Caja}'`,
        `(Posting_Date ge ${fechaInicio} and Posting_Date le ${fechaFin})`
    ];

    if (values.Turno) {  // Turno es el unico filtro opcional
        filters.push(`Turno eq ${values.Turno}`);
    }

    return `$filter=${filters.join(" and ")}`;
}

const mapDireccion2FromApi = (apiObj) =>
    apiObj.Bill_to_Address_2 ?? apiObj.Sell_to_Address_2 ?? apiObj.Direccion_2 ?? "";
// Mapping: API → IndexedDB schema
export const mapInvoice = (apiObj, countryCode) => {
    return {
        systemId: apiObj.systemId ?? apiObj.SystemId ?? "",
        No: "", //Factura no tiene 'No final' hasta contabilizar
        Pre_Assigned_No: apiObj.No ?? "",
        Id_TPV: apiObj.Id_TPV ?? "",
        Document_Type: apiObj.Document_Type ?? "",
        Sell_to_Customer_No_: apiObj.Sell_to_Customer_No ?? "",
        Sell_to_Customer_Name_2: apiObj.Sell_to_Customer_Name_2 ?? "",
        Razon_Social: apiObj.Razon_Social ?? "",
        Posting_No: apiObj.External_Document_No ?? "",
        draftNo: "",
        Colegio: apiObj.Cod_Colegio ?? "",
        Bill_to_Name: apiObj.Bill_to_Name ?? "",
        Bill_to_Address: apiObj.Bill_to_Address ?? "",
        Direccion_2: mapDireccion2FromApi(apiObj),
        Bill_to_City: apiObj.Bill_to_City ?? "",
        VAT_Registration_No_: apiObj.VAT_Registration_No ?? "",
        Bill_to_Country_Region_Code: apiObj.Bill_to_Country_Region_Code ?? "",
        Ship_to_Code: apiObj.Ship_to_Code ?? "",
        Tipo_Documento_SrI: resolveDocumentType(apiObj.Tipo_Documento_SrI, countryCode),
        E_Mail: apiObj.SellToEmail ?? apiObj.BillToContactEmail ?? apiObj.E_Mail ?? "",
        Telefono:
            apiObj.Sell_to_Phone ??
            apiObj.SellToPhoneNo ??
            apiObj.Phone_No ??
            apiObj.PhoneNo ??
            apiObj.Telefono ??
            "",
        Mobil: apiObj.SellToMobilePhoneNo ?? apiObj.MobilePhoneNo ?? apiObj.Mobil ?? "",
        caja: apiObj.Caja ?? "",
        clientLocalId: "",
        turno: apiObj.Turno ?? null,
        Dto: "",
        importeDto: "",
        Campaign_No_: apiObj.Campaign_No_ ?? apiObj.Campaign_No ?? "",
        Cupon: apiObj.Cupon ?? apiObj.Cod_Cupon ?? "",
        Tipo_Detalle: apiObj.Tipo_Detalle ?? "",
        No_Detalle: apiObj.No_Detalle ?? "",
        Payment_Method: apiObj.Payment_Method_Code ?? "",
        Establecimiento_Factura: apiObj.Establecimiento_Factura ?? "",
        Punto_de_Emision_Factura: apiObj.Punto_de_Emision_Factura ?? "",
        No_Comprobante_Fiscal_Rel: apiObj.No_Comprobante_Fiscal_Rel ?? "",
        No_Serie_NCF_Facturas: apiObj.No_Serie_NCF_Facturas ?? "",
        No_Serie_NCF_Abonos: apiObj.No_Serie_NCF_Abonos ?? "",
        Salesperson_Code: apiObj.Salesperson_Code ?? "",
        RTN: apiObj.VAT_Registration_No ?? "",
        pendingSync: 0,
        Posting_Date: apiObj.Posting_Date ?? "",
        lastModified: apiObj.Posting_Date ?? "",
        lastSynced: new Date().toISOString(),
        deleted: 0,
        syncing: 0,
        syncStartTime: null,
        syncStage: "payments",
        posted: 0
    };
};
export const mapPostedInvoice = (apiObj, documentType, countryCode) => {
    return {
        systemId: apiObj.systemId ?? apiObj.SystemId ?? "",
        No: apiObj.No ?? "",
        Document_Type: documentType,
        Sell_to_Customer_No_: apiObj.Sell_to_Customer_No ?? "",
        Sell_to_Customer_Name_2: apiObj.Sell_to_Customer_Name_2 ?? "",
        Razon_Social: apiObj.Razon_Social ?? "",
        Posting_No: apiObj.External_Document_No ?? "",
        draftNo: "",
        Colegio: apiObj.Cod_Colegio ?? "",
        Bill_to_Name: apiObj.Bill_to_Name ?? "",
        Bill_to_Address: apiObj.Bill_to_Address ?? "",
        Direccion_2: mapDireccion2FromApi(apiObj),
        Bill_to_City: apiObj.Bill_to_City ?? "",
        VAT_Registration_No_: apiObj.VAT_Registration_No ?? "",
        Bill_to_Country_Region_Code: apiObj.Bill_to_Country_Region_Code ?? "",
        Ship_to_Code: apiObj.Ship_to_Code ?? "",
        Tipo_Documento_SrI: resolveDocumentType(apiObj.Tipo_Documento, countryCode),
        E_Mail: apiObj.SellToEmail ?? apiObj.BillToContactEmail ?? apiObj.E_Mail ?? "",
        Telefono:
            apiObj.Sell_to_Phone ??
            apiObj.SellToPhoneNo ??
            apiObj.Phone_No ??
            apiObj.PhoneNo ??
            apiObj.Telefono ??
            "",
        Mobil: apiObj.SellToMobilePhoneNo ?? apiObj.MobilePhoneNo ?? apiObj.Mobil ?? "",
        caja: apiObj.Caja ?? apiObj.TPV ?? "",
        clientLocalId: "",
        turno: apiObj.Turno ?? null,
        Dto: "",
        importeDto: "",
        Cupon: apiObj.Cupon ?? apiObj.Cupon_de_descuento ?? apiObj.Cod_Cupon ?? "",
        Tipo_Detalle: apiObj.Tipo_Detalle ?? "",
        No_Detalle: apiObj.No_Detalle ?? "",
        Payment_Method: apiObj.Payment_Method_Code ?? "",
        Pre_Assigned_No: apiObj.Pre_Assigned_No ?? "",
        Establecimiento_Factura: apiObj.Establecimiento_Factura ?? "",
        Punto_de_Emision_Factura: apiObj.Punto_de_Emision_Factura ?? "",
        No_Serie_NCF_Facturas: apiObj.No_Serie_NCF_Facturas ?? "",
        No_Serie_NCF_Abonos: apiObj.No_Serie_NCF_Abonos ?? "",
        No_Comprobante_Fiscal: apiObj.No_Comprobante_Fiscal ?? "",
        No_Autorizacion_CAI: apiObj.No_Autorizacion_CAI ?? "",
        Campaign_No_: apiObj.Campaign_No ?? apiObj.Campaign_No_ ?? "",
        Salesperson_Code: apiObj.Salesperson_Code ?? "",
        RTN: apiObj.VAT_Registration_No ?? "",
        pendingSync: 0,
        Posting_Date: apiObj.Posting_Date ?? apiObj.Document_Date ?? "",
        lastModified: apiObj.Posting_Date ?? apiObj.Document_Date ?? "",
        lastSynced: new Date().toISOString(),
        deleted: 0,
        syncing: 0,
        syncStartTime: null,
        syncStage: "posted",
        posted: 1,
    };
};

// Mapping: invoices lines + credit memos lines + posted invoice lines
export const mapLine = (apiResponse) => {
    const lineAmount = apiResponse.Line_Amount ?? apiResponse.Amount ?? 0;

    const discountAmount = apiResponse.Line_Discount_Amount ?? 0;

    // Ignorar líneas que no son de negocio (comentarios, vacías, etc.)
    const isCommentLine =
        apiResponse.Type === " " ||
        apiResponse.FilteredTypeField === "Comment";

    // VAT 
    let vatAmount = 0;

    if (!isCommentLine) {
        if (apiResponse.Total_VAT_Amount != null) {
            vatAmount = apiResponse.Total_VAT_Amount;
        } else if (
            apiResponse.Amount_Including_VAT != null &&
            lineAmount != null
        ) {
            vatAmount = apiResponse.Amount_Including_VAT - lineAmount;
        }
    }

    // Descuento %
    let discountPercent =
        lineAmount + discountAmount > 0
            ? (discountAmount * 100) / (lineAmount + discountAmount)
            : 0;

    // IVA %
    let calculatedPorIva =
        lineAmount > 0 ? (vatAmount * 100) / lineAmount : 0;

    return {
        invoiceNo: apiResponse.Document_No,
        Line_No: apiResponse.Line_No,
        Type: apiResponse.Type,
        No: apiResponse.No,
        Description: apiResponse.Description,
        Quantity: apiResponse.Quantity,
        Unit_Price: apiResponse.Unit_Price,

        Line_Discount_: discountPercent,
        Line_Amount: lineAmount,

        PorIva: isCommentLine
            ? 0
            : apiResponse.Retention_VAT_Percent ??
            apiResponse.VAT_Percent ??
            calculatedPorIva,

        Total_VAT_Amount: vatAmount,

        pendingSync: 0,
        lastModified: new Date().toISOString(),
        lastSynced: new Date().toISOString(),
        deleted: 0,
    };
};

// Mapping: posted credit memos y facturas sin contabilizar
export const mapLineCalculateVat = (apiResponse) => {
    const lineAmount = apiResponse.Line_Amount ?? 0; // sin IVA
    const discountAmount = apiResponse.Line_Discount_Amount ?? 0;
    const amountIncludingVat = apiResponse.Amount_Including_VAT ?? 0;

    // Calcular porcentaje de descuento real (si Line_Amount ya está descontado)
    let discountPercent =
        lineAmount + discountAmount > 0
            ? (discountAmount * 100) / (lineAmount + discountAmount)
            : 0;

    // Calcular IVA solo si existe monto de IVA (> 0)
    let calculatedPorIva = lineAmount > 0 ? ((amountIncludingVat - lineAmount) * 100) / lineAmount : 0;

    let result = {
        invoiceNo: apiResponse.Document_No,
        Line_No: apiResponse.Line_No,
        Type: apiResponse.Type,
        No: apiResponse.No,
        Description: apiResponse.Description,
        Quantity: apiResponse.Quantity,
        Unit_Price: apiResponse.Unit_Price, // precio base sin IVA
        Line_Discount_: discountPercent, // % de descuento (lo que tu cálculo espera)
        Line_Amount: lineAmount, // sin IVA
        PorIva: calculatedPorIva,
        pendingSync: 0,
        lastModified: new Date().toISOString(),
        lastSynced: new Date().toISOString(),
        deleted: 0,
    };

    return result;
};

//Mapping: Payment details invoice + payment details credit memos
export function mapPaymentDetails(bcObj) {
    const baseAmount = parseFloat(bcObj?.Importe ?? 0);
    const discount = parseFloat(bcObj?.Descuento ?? 0);
    const discountPercentage = baseAmount > 0 ? (discount * 100) / baseAmount : 0;

    return {
        invoiceId: 0, // No existe en BC 
        Document_Type: bcObj?.Document_Type || "Invoice",
        Document_No_: bcObj?.Document_No_ || "",
        Line_No_: bcObj?.Line_No_ || 1,
        Payment_Method: bcObj?.Forma_de_Pago || "EFECTIVO",
        Amount: baseAmount,
        Dto: parseFloat(discountPercentage.toFixed(2)),
        Dto_Amount: discount,
        deleted: false,
        lastModified: new Date().toISOString(),
        lastSynced: new Date().toISOString(),
        pendingSync: 0
    };
}

// Excluye documentos con importe 0 Y sin formas de pago (aplica a todos los países).
export function filterEmptyDocuments({ documents, lines, payments, getLineKey, getPaymentKey }) {
    const amounts = lines.reduce((acc, l) => {
        if (l?.invoiceNo) acc.set(l.invoiceNo, (acc.get(l.invoiceNo) || 0) + (Number(l.Line_Amount) || 0));
        return acc;
    }, new Map());
    const paid = new Set(payments.map((p) => p?.Document_No_).filter(Boolean));

    const keepL = new Set(), keepP = new Set();
    const docs = documents.filter((d) => {
        const lk = getLineKey(d), pk = getPaymentKey(d);
        if (Math.abs(amounts.get(lk) || 0) < 0.01 && (!pk || !paid.has(pk))) return false;
        if (lk) keepL.add(lk);
        if (pk) keepP.add(pk);
        return true;
    });

    return {
        documents: docs,
        lines: lines.filter((l) => keepL.has(l?.invoiceNo)),
        payments: payments.filter((p) => keepP.has(p?.Document_No_)),
    };
}

// Chunk logic -> crea lotes de documentos para hacer multiples llamadas (Una sola = Network error)
export function chunkArray(array, size) {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
        result.push(array.slice(i, i + size));
    }
    return result;
}

export async function fetchLinesInChunks(
    endpoint,
    documents,
    mapFn,
    chunkSize = 35,
    filterDocumentLabel = 'Document_No',
    usePreAssigned = false) {
    const chunks = chunkArray(documents, chunkSize);
    let allResults = [];

    for (const chunk of chunks) {
        const filter = chunk
            .map((doc) => {
                const value = usePreAssigned ? doc.Pre_Assigned_No : doc.No;
                return `${filterDocumentLabel} eq '${value}'`;
            })
            .join(" or ");

        const response = await fetchFromBusinessCentral(
            `${endpoint}?$filter=${filter}`
        );

        allResults.push(...response.map(mapFn));
    }

    return allResults;
}

// Mapea respuestas en formato que espera el componente Select de Antd
export const mapApiOptionsResponse = (apiResponse) => {
    if (!apiResponse || !Array.isArray(apiResponse)) {
        return [];
    }

    return apiResponse.map(item => {
        // Detectar el campo de ID según la API
        const id =
            item["Cód_Departamento_FE"] ||
            item["Cód_Distrito_FE"] ||
            item["Cód_Ciudad_FE"] ||
            item["Code"] ||
            null;

        // Detectar el campo de descripción o nombre
        const label =
            item["Descripción"] ||
            item["Description"] ||
            item["Name"] ||
            "";

        return {
            label,
            value: id,
        };
    });
}

// Función para emitir eventos de sincronización
export const emitSyncEvent = (message, isComplete = false) => {
    const event = new CustomEvent('syncStatus', {
        detail: { message, isComplete }
    });
    window.dispatchEvent(event);
};
